/**
 * ---------------------------------------------------------------------------
 * VENNER — Product page video strip
 * ---------------------------------------------------------------------------
 *
 * Only one thumbnail is active at a time: it grows, plays, and when its turn
 * is over hands off to the next one. A turn lasts as long as the setting says,
 * or as long as the video itself if the setting is 0.
 *
 * Everything starts only once the strip scrolls into view, and stops when it
 * leaves, so no data is wasted on videos nobody is watching.
 */

{
  class VennerVideoStrip extends HTMLElement {
    #lastManualScroll = 0;
    #autoScrolling = false;
    #autoScrollTimer = 0;

    #abortController;
    #observer;
    #preloadObserver;
    #timer;
    #index = 0;
    #inView = false;

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.vvs__item'));
      this.trackEl = this.querySelector('.vvs__track');

      if (!this.items.length) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Manual scrolling takes priority: auto-scroll must not fight the visitor.
      this.trackEl?.addEventListener('scroll', () => {
        if (!this.#autoScrolling) this.#lastManualScroll = Date.now();
      }, { passive: true, signal });

      this.captionEl = this.querySelector('[data-vvs-caption]');

      this.items.forEach((item, i) => {
        item.addEventListener('click', (event) => {
          // If the thumbnail is a link, the first click only activates it;
          // once active, the click goes through to the link.
          if (item.dataset.href && item.classList.contains('is-active')) return;
          event.preventDefault();
          this.activate(i, { fromUser: true });
        }, { signal });
      });

      this.querySelectorAll('[data-vvs-sound]').forEach((button) => {
        button.addEventListener('click', () => this.toggleSound(), { signal });
      });

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.activate(this.#index);
            } else {
              this.stop();
            }
          }
        },
        { threshold: 0.35 }
      );

      this.#observer.observe(this);

      /*
        Videos are rendered with preload="none" so they don't all download at once on mobile. This
        second observer runs 600px ahead of the section and requests the first ones, so they're ready
        on arrival instead of only starting to load at that moment.
      */
      this.#preloadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#showPosters();
            this.#preloadFrom(0);
            this.#preloadObserver.disconnect();
          }
        },
        { rootMargin: '600px 0px' }
      );

      this.#preloadObserver.observe(this);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
      this.#preloadObserver?.disconnect();
      clearTimeout(this.#timer);
    }

    /**
     * The covers are lazy, and a lazy image inside a horizontal scroller only loads once it's
     * scrolled into the track — so the thumbnails past the third one sat there as empty grey
     * boxes until the visitor swiped. They're a few kilobytes each, so once the section is
     * close they all get asked for at once. Switching `loading` off lazy starts the download.
     */
    #showPosters() {
      this.querySelectorAll('.vvs__poster img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    get turnDuration() {
      return parseFloat(this.getAttribute('duration')) || 0;
    }

    get playAll() {
      return this.hasAttribute('play-all');
    }

    get soundOn() {
      return this.hasAttribute('sound-on');
    }

    /**
     * Brings the active video to the center of the track as the turns advance, like the video strips
     * in social apps: the ones that don't fit stay out of view until it's their turn.
     *
     * Moves the track's `scrollLeft` instead of using scrollIntoView, which would drag the page too.
     * If the visitor just scrolled manually, it yields to them for a few seconds.
     */
    #centerActive({ immediate = false } = {}) {
      const track = this.trackEl;
      const item = this.items[this.#index];

      if (!track || !item || track.scrollWidth <= track.clientWidth) return;
      if (!immediate && Date.now() - this.#lastManualScroll < 4000) return;

      const target = item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#autoScrolling = true;
      track.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
      clearTimeout(this.#autoScrollTimer);
      this.#autoScrollTimer = setTimeout(() => { this.#autoScrolling = false; }, 700);
    }

    get preloadAhead() {
      const value = parseInt(this.getAttribute('preload-ahead'), 10);

      return Number.isFinite(value) ? Math.min(3, Math.max(0, value)) : 1;
    }

    /**
     * Requests the upcoming videos while the current one plays, so the turn change is instant.
     * Without this, each video only starts downloading when its turn comes and the visitor sees a
     * couple of blank seconds.
     */
    #preloadFrom(index) {
      const count = this.preloadAhead;

      for (let offset = 0; offset <= count; offset += 1) {
        const item = this.items[(index + offset) % this.items.length];
        const video = item?.querySelector('video');

        if (!video || video.dataset.preloaded === 'yes') continue;

        video.dataset.preloaded = 'yes';
        video.preload = 'auto';

        // load() aborts any playback in progress: only call it if the video is untouched.
        if (video.paused && video.readyState === 0 && video.currentTime === 0) {
          video.load();
        }
      }
    }

    get playbackRate() {
      const value = parseFloat(this.getAttribute('playback-rate'));

      // Clamped to the setting's range so an odd value can't leave the video unusable.
      return Number.isFinite(value) ? Math.min(2, Math.max(0.5, value)) : 1;
    }

    /**
     * Some browsers reset playbackRate when metadata loads, so it's also applied once the video is
     * ready, not just before playing it.
     */
    #applyPlaybackRate(video) {
      const rate = this.playbackRate;

      video.playbackRate = rate;

      if (video.readyState === 0) {
        video.addEventListener('loadedmetadata', () => { video.playbackRate = rate; }, { once: true });
      }
    }

    activate(index, { fromUser = false } = {}) {
      clearTimeout(this.#timer);
      this.#index = (index + this.items.length) % this.items.length;

      this.items.forEach((item, i) => {
        const isActive = i === this.#index;
        const video = item.querySelector('video');

        item.classList.toggle('is-active', isActive);

        if (!video) return;

        if (isActive) {
          video.muted = !this.soundOn;
          video.currentTime = 0;
          this.#applyPlaybackRate(video);
          this.#reflowProgress(item);
          video.play().then(() => item.classList.add('is-playing')).catch(() => {
            /* the browser blocked playback: the cover image stays */
          });
        } else {
          video.muted = true;
          if (this.playAll) {
            this.#applyPlaybackRate(video);
            video.play().then(() => item.classList.add('is-playing')).catch(() => {});
          } else {
            video.pause();
            item.classList.remove('is-playing');
          }
        }
      });

      this.#updateCaption();
      this.#centerActive({ immediate: fromUser });
      this.#preloadFrom(this.#index + 1);

      if (!this.#inView && !fromUser) return;

      const activeVideo = this.items[this.#index].querySelector('video');

      if (this.turnDuration > 0) {
        this.#timer = setTimeout(() => this.next(), this.turnDuration * 1000);
      } else if (activeVideo) {
        // With no fixed duration, the turn ends when the video ends.
        activeVideo.onended = () => this.next();
      }
    }

    next() {
      this.activate(this.#index + 1);
    }

    stop() {
      clearTimeout(this.#timer);
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (video) video.pause();
      });
    }

    toggleSound() {
      const turningOn = !this.soundOn;
      this.toggleAttribute('sound-on', turningOn);

      // Only the active thumbnail plays sound, and never two strips at once.
      if (turningOn) {
        document.querySelectorAll('venner-video-strip[sound-on]').forEach((strip) => {
          if (strip !== this) strip.toggleSound();
        });
      }

      const activeVideo = this.items[this.#index]?.querySelector('video');
      if (activeVideo) activeVideo.muted = !turningOn;
    }

    #updateCaption() {
      if (!this.captionEl) return;
      this.captionEl.textContent = this.items[this.#index].dataset.caption || '';
    }

    /* Restarts the progress bar animation on every turn. */
    #reflowProgress(item) {
      const bar = item.querySelector('.vvs__progress > span');
      if (!bar) return;
      bar.style.animation = 'none';
      void bar.offsetWidth;
      bar.style.animation = '';
    }
  }

  if (!window.customElements.get('venner-video-strip')) {
    window.customElements.define('venner-video-strip', VennerVideoStrip);
  }
}
